/**
 * Command Center constants and lightweight helpers.
 * Extracted from CommandCenter.js for better discoverability and to keep the
 * main page component focused on orchestration.
 */

// Virtualization config — switch to react-window when bucket grows large
export const VIRTUALIZE_THRESHOLD = 30;
export const ROW_HEIGHT = 84; // card (~76px) + 8px vertical gap
export const HIGHLIGHT_DURATION_MS = 2200;

// Drill-down: which KPI tiles open a records list
// (conversion is a derived ratio with no underlying list)
export const DRILLABLE_METRICS = ['submissions', 'interviews', 'closures', 'tat', 'pending', 'pending_paperwork', 'pending_joining', 'revenue'];

export const DRILL_TITLES = {
  submissions: 'Submissions',
  interviews: 'Pending Interviews',
  closures: 'Closures',
  tat: 'Avg. Time-to-Hire',
  pending: 'Pending Review',
  pending_paperwork: 'Pending Offer Paperwork',
  pending_joining: 'Pending Joining Confirmation',
  revenue: 'Revenue',
};

// Backend stage IDs mapped to UI labels per product spec.
// Sourced → Screened → Client Submission → Interview → Selected → Offered → Joined  + Rejected + On Hold + Dropped
export const STAGES = [
  { id: 'sourced', label: 'Sourced', dot: 'bg-slate-400', col: 'bg-slate-50/60 border-slate-200' },
  { id: 'screening', label: 'Screened', dot: 'bg-sky-500', col: 'bg-sky-50/60 border-sky-200' },
  { id: 'client_submission', label: 'Client Submission', dot: 'bg-violet-500', col: 'bg-violet-50/60 border-violet-200' },
  { id: 'interview_scheduled', label: 'Interview', dot: 'bg-amber-500', col: 'bg-amber-50/60 border-amber-200' },
  { id: 'interview_selected', label: 'Selected', dot: 'bg-emerald-500', col: 'bg-emerald-50/60 border-emerald-200' },
  { id: 'offer_released', label: 'Offered', dot: 'bg-teal-500', col: 'bg-teal-50/60 border-teal-200' },
  { id: 'joined', label: 'Joined', dot: 'bg-green-600', col: 'bg-green-50/70 border-green-300' },
  { id: 'rejected', label: 'Rejected', dot: 'bg-red-500', col: 'bg-red-50/60 border-red-200', destructive: true },
  { id: 'dropped', label: 'On Hold', dot: 'bg-orange-500', col: 'bg-orange-50/60 border-orange-200', destructive: true },
  // Candidate withdrew permanently — distinct from "On Hold" (paused, may resume).
  { id: 'dropped_out', label: 'Dropped', dot: 'bg-rose-600', col: 'bg-rose-50/60 border-rose-200', destructive: true },
];

export const STAGE_BY_ID = STAGES.reduce((acc, s) => ({ ...acc, [s.id]: s }), {});

/** Format an INR number with K / L / Cr suffixes. Returns null on bad input. */
export const formatINR = (n) => {
  if (!n && n !== 0) return null;
  const num = Number(n);
  if (Number.isNaN(num)) return null;
  if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)} K`;
  return `${num}`;
};

/** Tailwind classes for the temperature badge by variant. */
export const tempBadge = (text, variant = 'cool') => {
  const map = {
    hot: 'bg-red-50 text-red-700 border-red-200',
    warm: 'bg-amber-50 text-amber-700 border-amber-200',
    cool: 'bg-sky-50 text-sky-700 border-sky-200',
  };
  return map[variant] || map.cool;
};

/** Heuristic: hot if joined/offered/selected, warm if shortlisted/interview, cool otherwise */
export const getCandidateTemperature = (app) => {
  if (['offer_released', 'interview_selected', 'joined'].includes(app.stage)) return 'hot';
  if (['client_submission', 'interview_scheduled'].includes(app.stage)) return 'warm';
  return 'cool';
};
